import { ButtonStylesParams, MantineTheme, rem } from "@mantine/core";
import { DEFAULT_COLORS } from "./default-colors";

const mapFilledColorToTextColor = (
  filledColor: string,
  theme: MantineTheme,
) => {
  switch (filledColor) {
    case "primary":
      return "white";
    case "secondary":
      return "white";
    case "tertiary":
      return theme.colors.tertiary[9];
    case "white":
      return "black";
    default:
      return "white";
  }
};

const mapColorParamsToBackgroundColor = (
  colorParams: string,
  theme: MantineTheme,
) => {
  switch (colorParams) {
    case "primary":
      return theme.colors.primary[7];
    case "secondary":
      return colorParams;
    case "tertiary":
      return theme.colors.tertiary[0];
    case "white":
      return theme.colors.white[0];
    default:
      return "";
  }
};

// eslint-disable-next-line no-underscore-dangle, import/prefer-default-export
export const DEFAULT_THEME: any = {
  dir: "ltr",
  primaryShade: {
    light: 6,
    dark: 8,
  },
  focusRing: "auto",
  loader: "dots",
  colorScheme: "light",
  white: "#fff",
  black: "#000",
  defaultRadius: "sm",
  transitionTimingFunction: "ease",
  colors: DEFAULT_COLORS,
  lineHeight: 1.55,
  // fontFamily:
  //   "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji",
  fontFamily: "Manrope",
  fontFamilyMonospace:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
  primaryColor: "primary",
  respectReducedMotion: true,
  cursorType: "default",
  defaultGradient: {
    from: "indigo",
    to: "cyan",
    deg: 45,
  },

  shadows: {
    xs: "0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.1)",
    sm: "0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 0.625rem 0.9375rem -0.3125rem, rgba(0, 0, 0, 0.04) 0 0.4375rem 0.4375rem -0.3125rem",
    md: "0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 1.25rem 1.5625rem -0.3125rem, rgba(0, 0, 0, 0.04) 0 0.625rem 0.625rem -0.3125rem",
    lg: "0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 1.75rem 1.4375rem -0.4375rem, rgba(0, 0, 0, 0.04) 0 0.75rem 0.75rem -0.4375rem",
    xl: "0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 2.25rem 1.75rem -0.4375rem, rgba(0, 0, 0, 0.04) 0 1.0625rem 1.0625rem -0.4375rem",
  },

  fontSizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
  },

  radius: {
    xs: "0.125rem",
    sm: "0.25rem",
    md: "0.5rem",
    lg: "1rem",
    xl: "2rem",
  },

  spacing: {
    xs: "0.625rem",
    sm: "0.75rem",
    md: "1rem",
    lg: "1.25rem",
    xl: "1.5rem",
  },

  breakpoints: {
    xs: "36em",
    sm: "48em",
    md: "62em",
    lg: "75em",
    xl: "88em",
  },

  headings: {
    // fontFamily:
    //   "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji",
    fontFamily: "Manrope",
    fontWeight: 700,
    sizes: {
      h1: { fontSize: "2.125rem", lineHeight: 1.3, fontWeight: undefined },
      h2: { fontSize: "1.625rem", lineHeight: 1.35, fontWeight: undefined },
      h3: { fontSize: "1.375rem", lineHeight: 1.4, fontWeight: undefined },
      h4: { fontSize: "1.125rem", lineHeight: 1.45, fontWeight: undefined },
      h5: { fontSize: "1rem", lineHeight: 1.5, fontWeight: undefined },
      h6: { fontSize: "0.875rem", lineHeight: 1.5, fontWeight: undefined },
    },
  },

  other: {},
  components: {
    // JVC-7004: droplist cho MỌI luồng (tạo/xem chi tiết để duyệt/từ chối...) phải bung XUỐNG DƯỚI
    // và render ở body (Portal) để không bị Card/khối overflow:hidden cắt mất giá trị chọn.
    Select: {
      defaultProps: {
        withinPortal: true,
        dropdownPosition: "bottom",
      },
    },
    Button: {
      styles: (
        theme: MantineTheme,
        params: ButtonStylesParams,
        { variant }: any,
      ) => ({
        root: {
          borderRadius: theme.radius.xl,
          fontSize: params.compact && theme.fontSizes.xs,
          backgroundColor: mapColorParamsToBackgroundColor(params.color, theme),
          color:
            variant === "filled"
              ? mapFilledColorToTextColor(params.color, theme)
              : params.color,
          boxShadow:
            params.color === "tertiary" && "rgba(0, 0, 0, 0.16) 0px 1px 4px",
        },
      }),
    },
    Modal: {
      styles: (
        theme: MantineTheme,
        params: ButtonStylesParams,
        { variant }: any,
      ) => ({
        content: {
          borderRadius: theme.radius.lg,
        },
      }),
    },
    ScrollArea: {
      styles: (
        theme: MantineTheme,
        params: ButtonStylesParams,
        { variant }: any,
      ) => ({
        scrollbar: {
          width: rem(3),
        },
        thumb: {
          background: theme.colors.primary[7],
          borderRadius: theme.radius.xd,
        },
      }),
    },
    Table: {
      styles: (theme: MantineTheme) => ({
        root: {
          border: `1px solid ${theme.colors.gray[3]}`,
          borderRadius: theme.radius.md,
          overflow: "hidden",
          thead: {
            height: "45px",
            tr: {
              background: "#f8f9fb",
              th: {
                fontWeight: 600,
                fontSize: theme.fontSizes.sm,
                padding: `12px 10px !important`,
                borderBottom: `1px solid ${theme.colors.gray[3]}`,
              },
            },
          },
          tr: {
            transition: "background 0.2s ease",
            backgroundColor: theme.colors.white[0],
            td: {
              fontSize: theme.fontSizes.sm,
              padding: `12px 10px !important`,
            },
          },
        },
      }),
    },
    Pagination: {
      styles: (theme: MantineTheme) => ({
        control: {
          color: "#6B7280",
          backgroundColor: "transparent",
          border: "none",
          fontSize: "16px",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: "#E5F4FE",
            color: "#2A81D0",
          },
          "&[data-active]": {
            backgroundColor: "#E5F4FE",
            color: "#2A81D0",
            pointerEvents: "none",
          },
          "&:first-of-type, &:last-of-type": {
            backgroundColor: "transparent !important",
            "&:hover": {
              backgroundColor: "transparent !important",
            },
          },
        },
      }),
    },
    Container: {
      defaultProps: {
        sizes: {
          xs: 540,
          sm: 720,
          md: 960,
          lg: 1140,
          xl: 1320,
        },
      },
    },
    Tabs: {
      styles: (theme: MantineTheme) => ({
        tab: {
          "&[data-active]": {
            color: "#2A81D0",
          },
        },
      }),
    },
  },
  activeStyles: { transform: "translateY(0.0625rem)" },
  datesLocale: "en",
};
